import React, { Suspense, lazy } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Outlet, useNavigate, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { CartProvider } from "@/context/CartContext";
import { CurrencyProvider } from "@/context/CurrencyContext";
import { Header } from "@/components/Header";
import { SiteNoticeBanner } from "@/components/SiteNoticeBanner";
import { SandboxBanner, SandboxGate } from "@/components/Sandbox";
import { MaintenanceBanner } from "@/components/MaintenanceBanner";
import { Footer } from "@/components/Footer";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { ChatWidget } from "@/components/ChatWidget";
import { RouteTracker } from "@/components/RouteTracker";
import { AccessibilityWidget } from "@/components/AccessibilityWidget";
import { TermsBanner } from "@/components/TermsBanner";
import { CookieNotice } from "@/components/CookieNotice";
import AuthCallback from "@/pages/AuthCallback";

// The homepage ships in the main bundle; every other page is its own chunk, fetched the first time it's opened.
// Shoppers no longer download the admin/vendor dashboards (the bulk of the app) just to browse.
import Home from "@/pages/Home";
const Products = lazy(() => import("@/pages/Products"));
const ProductDetail = lazy(() => import("@/pages/ProductDetail"));
const Cart = lazy(() => import("@/pages/Cart"));
const Wishlist = lazy(() => import("@/pages/Wishlist"));
const Checkout = lazy(() => import("@/pages/Checkout"));
const OrderConfirmation = lazy(() => import("@/pages/OrderConfirmation"));
const TrackOrder = lazy(() => import("@/pages/TrackOrder"));
const Account = lazy(() => import("@/pages/Account"));
const Login = lazy(() => import("@/pages/Login"));
const Register = lazy(() => import("@/pages/Register"));
const VendorDashboard = lazy(() => import("@/pages/VendorDashboard"));
const AdminDashboard = lazy(() => import("@/pages/AdminDashboard"));
const StorePage = lazy(() => import("@/pages/StorePage"));
const Templates = lazy(() => import("@/pages/Templates"));
const LiveShopping = lazy(() => import("@/pages/LiveShopping"));
const LiveWatch = lazy(() => import("@/pages/LiveWatch"));
const ReturnsPolicy = lazy(() => import("@/pages/ReturnsPolicy"));
const DeliveryInfo = lazy(() => import("@/pages/DeliveryInfo"));
const Faq = lazy(() => import("@/pages/Faq"));
const ForgotPassword = lazy(() => import("@/pages/ForgotPassword"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const VerifyEmail = lazy(() => import("@/pages/VerifyEmail"));
const Invoice = lazy(() => import("@/pages/Invoice"));
const Community = lazy(() => import("@/pages/Community"));
const TeamDashboard = lazy(() => import("@/pages/TeamDashboard"));
const Privacy = lazy(() => import("@/pages/Privacy"));
const Contact = lazy(() => import("@/pages/Contact"));
const Terms = lazy(() => import("@/pages/Terms"));
const Responsibilities = lazy(() => import("@/pages/Responsibilities"));
const Advertise = lazy(() => import("@/pages/Advertise"));
const AdPay = lazy(() => import("@/pages/AdPay"));
const SellOnMarque = lazy(() => import("@/pages/SellOnMarque"));
const RestrictedPolicy = lazy(() => import("@/pages/RestrictedPolicy"));
const HmrcGuide = lazy(() => import("@/pages/HmrcGuide"));
const SafetyDocs = lazy(() => import("@/pages/SafetyDocs"));
const WorkplaceDocs = lazy(() => import("@/pages/WorkplaceDocs"));
const TemplatesRange = lazy(() => import("@/pages/TemplatesRange"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const CardsShop = lazy(() => import("@/pages/CardsShop"));
const CardSend = lazy(() => import("@/pages/CardSend"));
const CardView = lazy(() => import("@/pages/CardView"));
const About = lazy(() => import("@/pages/About"));
const GiftVouchers = lazy(() => import("@/pages/GiftVouchers"));
const VoucherThanks = lazy(() => import("@/pages/GiftVouchers").then((m) => ({ default: m.VoucherThanks })));
const PetPortraits = lazy(() => import("@/pages/PetPortraits"));
const PetCreate = lazy(() => import("@/pages/PetCreate"));
const WallArt = lazy(() => import("@/pages/WallArt"));
const BrandStudio = lazy(() => import("@/pages/BrandStudio"));
const BrandStudioCreate = lazy(() => import("@/pages/BrandStudioCreate"));
const PortraitShare = lazy(() => import("@/pages/PortraitShare"));
const AccessibilityPage = lazy(() => import("@/pages/AccessibilityPage"));
const VerifyAge = lazy(() => import("@/pages/VerifyAge"));
const StatusPage = lazy(() => import("@/pages/StatusPage"));
const ComingSoon = lazy(() => import("@/pages/ComingSoon"));
const WatchPage = lazy(() => import("@/pages/WatchPage"));

const PageLoader = () => (
  <div className="flex-1 flex items-center justify-center py-24" role="status" aria-live="polite" data-testid="page-loader">
    <div className="flex items-center gap-3 text-sm text-neutral-500">
      <span className="inline-block w-2.5 h-2.5 bg-[#0047AB] animate-pulse" />
      Loading…
    </div>
  </div>
);

function Shell() {
  const { user, loading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [comingSoon, setComingSoon] = React.useState(false);
  React.useEffect(() => {
    api.get("/public/coming-soon").then((r) => setComingSoon(!!r.data.enabled)).catch(() => {});
  }, []);
  React.useEffect(() => {
    if (loading || !comingSoon) return;
    const bypass = user && ["admin", "vendor", "vendor_staff"].includes(user.role);
    if (!bypass && !location.pathname.startsWith("/status")) navigate("/coming-soon", { replace: true });
  }, [comingSoon, loading, user, location.pathname, navigate]);
  return (
    <div className="min-h-screen flex flex-col">
      <a href="#main-content" className="skip-link">Skip to main content</a>
      <SandboxBanner />
      <SiteNoticeBanner />
      <MaintenanceBanner />
      <TermsBanner />
      <Header />
      <main id="main-content" className="flex-1"><Suspense fallback={<PageLoader />}><Outlet /></Suspense></main>
      <Footer />
      <CookieNotice />
      <RouteTracker />
      <ChatWidget />
      <AccessibilityWidget />
      <SandboxGate />
    </div>
  );
}

function AppRoutes() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route element={<Shell />}>
          <Route path="/" element={<Home />} />
          <Route path="/products" element={<Products />} />
          <Route path="/product/:slug" element={<ProductDetail />} />
          <Route path="/store/:slug" element={<StorePage />} />
          <Route path="/templates" element={<Templates />} />
          <Route path="/live" element={<LiveShopping />} />
          <Route path="/live/:id" element={<LiveWatch />} />
          <Route path="/cart" element={<Cart />} />
          <Route path="/wishlist" element={<Wishlist />} />
          <Route path="/checkout" element={<Checkout />} />
          <Route path="/order-confirmation" element={<OrderConfirmation />} />
          <Route path="/track" element={<TrackOrder />} />
          <Route path="/invoice" element={<Invoice />} />
          <Route path="/invoice/:orderId" element={<Invoice />} />
          <Route path="/returns" element={<ReturnsPolicy />} />
          <Route path="/delivery" element={<DeliveryInfo />} />
          <Route path="/faq" element={<Faq />} />
          <Route path="/community" element={<Community />} />
          <Route path="/team" element={<TeamDashboard />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/responsibilities" element={<Responsibilities />} />
          <Route path="/advertise" element={<Advertise />} />
          <Route path="/advertise/pay/:adId" element={<AdPay />} />
          <Route path="/sell" element={<SellOnMarque />} />
          <Route path="/restricted-policy" element={<RestrictedPolicy />} />
          <Route path="/hmrc-guide" element={<HmrcGuide />} />
          <Route path="/safety-documents" element={<SafetyDocs />} />
          <Route path="/workplace-documents" element={<WorkplaceDocs />} />
          <Route path="/mrqk-templates" element={<TemplatesRange />} />
          <Route path="/fitness-packs" element={<Navigate to="/workplace-documents?cat=fitness" replace />} />
          <Route path="/cards" element={<CardsShop />} />
          <Route path="/cards/send" element={<CardSend />} />
          <Route path="/card/:id" element={<CardView />} />
          <Route path="/about" element={<About />} />
          <Route path="/gift-vouchers" element={<GiftVouchers />} />
          <Route path="/gift-vouchers/thanks" element={<VoucherThanks />} />
          <Route path="/pet-portraits" element={<PetPortraits />} />
          <Route path="/pet-portraits/create" element={<PetCreate />} />
          <Route path="/wall-art" element={<WallArt />} />
          <Route path="/how-to-print" element={<WallArt />} />
          <Route path="/brand-studio" element={<BrandStudio />} />
          <Route path="/brand-studio/create" element={<BrandStudioCreate />} />
          <Route path="/portrait/:jobId" element={<PortraitShare />} />
          <Route path="/accessibility" element={<AccessibilityPage />} />
          <Route path="/verify-age" element={<VerifyAge />} />
          <Route path="/status" element={<StatusPage />} />
          <Route path="/watch/:slug" element={<WatchPage />} />
          <Route path="/account" element={<ProtectedRoute><Account /></ProtectedRoute>} />
          <Route path="/vendor" element={<ProtectedRoute><VendorDashboard /></ProtectedRoute>} />
          <Route path="/admin" element={<ProtectedRoute roles={["admin"]}><AdminDashboard /></ProtectedRoute>} />
          <Route path="*" element={<NotFound />} />
        </Route>
        <Route path="/login" element={<Login />} />
        <Route path="/coming-soon" element={<ComingSoon />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
      </Routes>
    </Suspense>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <CartProvider>
        <BrowserRouter>
          <Toaster position="top-right" richColors />
          <CurrencyProvider>
            <AppRoutes />
          </CurrencyProvider>
        </BrowserRouter>
      </CartProvider>
    </AuthProvider>
  );
}
